import React, { createContext, useState, useEffect } from 'react';
import unitedstates from '../constants/states';
import canada from '../constants/provinces';
import australia from '../constants/australia';
import jpStates from '../constants/jp-states';
import mexico from '../constants/mx-states';
import brazil from '../constants/br-states';
import uae from '../constants/me-states';
import philippines from '../constants/ph-provinces';
import netherlands from '../constants/netherlands';
interface LocalizationContextType {
    locale: string;
    localeWith_: string;
    defaultCurrency: string;
    currencySign: string;
    stateDropdown: boolean;
    postalCodeRequired: boolean;
    countryList: { ISO_CC: string; countryName: string }[];
    countyField: boolean;
    states?: { code: string; name: string; en?: string }[];
    showTax: boolean;
}
interface LocalizationProviderProps {
    children: React.ReactNode;
}
type LocaleKey = keyof typeof Localization;


const Localization = Object.freeze({
    'en-us': {
        locale: "en-us",
        localeWith_: "en_US",
        defaultCurrency: "USD",
        currencySign: "$",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: false,
        countryList: [
            { ISO_CC: "US", countryName: "United States" }
        ],
        states: unitedstates,
        showTax: true
    },
    'en-ca': {
        locale: "en-ca",
        localeWith_: "en_CA",
        defaultCurrency: "CAD",
        currencySign: "C$",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: false,
        countryList: [
            { ISO_CC: "CA", countryName: "Canada" }
        ],
        states: canada,
        showTax: true
    },
    'ja-jp': {
        locale: "ja-jp",
        localeWith_: "ja_JP",
        defaultCurrency: "JPY",
        currencySign: "¥",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: false,
        countryList: [
            { ISO_CC: "JP", countryName: "日本" }
        ],
        states: jpStates,
        showTax: false
    },
    'en-gb': {
        locale: "en-gb",
        localeWith_: "en_GB",
        defaultCurrency: "GBP",
        currencySign: "£",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "GB", countryName: "United Kingdom" }
        ],
        states: [],
        showTax: true
    },
    'de-de': {
        locale: "de-de",
        localeWith_: "de_DE",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "DE", countryName: "Deutschland" },
            { ISO_CC: "AT", countryName: "Österreich" },
            { ISO_CC: "LU", countryName: "Luxembourg" }
        ],
        states: [],
        showTax: true
    },
    'fr-fr': {
        locale: "fr-fr",
        localeWith_: "fr_FR",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "FR", countryName: "France" },
            { ISO_CC: "BE", countryName: "Belgium" },
            { ISO_CC: "LU", countryName: "Luxembourg" }
        ],
        states: [],
        showTax: true
    },
    'es-es': {
        locale: "es-es",
        localeWith_: "es_ES",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "ES", countryName: "España" }
        ],
        states: [],
        showTax: true
    },
    'it-it': {
        locale: "it-it",
        localeWith_: "it_IT",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "IT", countryName: "Italia" }
        ],
        states: [],
        showTax: true
    },
    'en-eu': {
        locale: "en-eu",
        localeWith_: "en_EU",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "IE", countryName: "Ireland" },
            { ISO_CC: "LU", countryName: "Luxembourg" },
            { ISO_CC: "PT", countryName: "Portugal" },
            { ISO_CC: "BG", countryName: "Bulgaria" },
            { ISO_CC: "HR", countryName: "Croatia" },
            { ISO_CC: "CY", countryName: "Cyprus" },
            { ISO_CC: "CZ", countryName: "Czech Republic" },
            { ISO_CC: "EE", countryName: "Estonia" },
            { ISO_CC: "GR", countryName: "Greece" },
            { ISO_CC: "HU", countryName: "Hungary" },
            { ISO_CC: "LT", countryName: "Lithuania" },
            { ISO_CC: "MT", countryName: "Malta" },
            { ISO_CC: "RO", countryName: "Romania" },
            { ISO_CC: "SK", countryName: "Slovakia" },
            { ISO_CC: "SI", countryName: "Slovenia" },
            { ISO_CC: "AT", countryName: "Austria" },
            { ISO_CC: "BE", countryName: "Belgium" },
            { ISO_CC: "NL", countryName: "Netherlands" },
            { ISO_CC: "PL", countryName: "Poland" }
        ],
        states: [],
        showTax: true
    },
    'en-au': {
        locale: "en-au",
        localeWith_: "en_AU",
        defaultCurrency: "AUD",
        currencySign: "AU$",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: false,
        countryList: [
            { ISO_CC: "AU", countryName: "Australia" }
        ],
        states: australia,
        showTax: false
    },
    'en-sg': {
        locale: "en-sg",
        localeWith_: "en_SG",
        defaultCurrency: "SGD",
        currencySign: "S$",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "SG", countryName: "Singapore" }
        ],
        states: [],
        showTax: false
    },
    'vi-vn': {
        locale: "vi-vn",
        localeWith_: "vi_VN",
        defaultCurrency: "VND",
        currencySign: "₫",
        stateDropdown: false,   
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "VN", countryName: "Vietnam" }
        ],
        states: [],
        showTax: false
    },
    'ko-kr': {
        locale: "ko-kr",
        localeWith_: "ko_KR",
        defaultCurrency: "KRW",
        currencySign: "₩",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            {
                ISO_CC: "KR", countryName: "대한민국(South Korea)"
            }
        ],
        states: [],
        showTax: false
    },
    'zh-tw': {
        locale: "zh-tw",
        localeWith_: "zh_TW",
        defaultCurrency: "TWD",
        currencySign: "NT$",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "TW", countryName: "台灣" }
        ],
        states: [],
        showTax: false
    },
    'es-mx': {
        locale: "es-mx",
        localeWith_: "es_MX",
        defaultCurrency: "MXN",
        currencySign: "MX$",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "MX", countryName: "Mexico" }
        ],
        states: mexico,
        showTax: true
    },
    'pt-br': {
        locale: "pt-br",
        localeWith_: "pt_BR",
        defaultCurrency: "BRL",
        currencySign: "R$",
        stateDropdown: true,    
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "BR", countryName: "Brazil" }
        ],
        states: brazil,
        showTax: true
    },
    'en-hk': {
        locale: "en-hk",
        localeWith_: "en_HK",
        defaultCurrency: "HKD",
        currencySign: "HK$",
        stateDropdown: false,   
        postalCodeRequired: false,
        countyField: true,
        countryList: [
            { ISO_CC: "HK", countryName: "Hong Kong" }
        ],
        states: [],
        showTax: false
    },
    'en-me': {
        locale: "en-me",    
        localeWith_: "en_ME",
        defaultCurrency: "USD",
        currencySign: "$",
        stateDropdown: true,
        postalCodeRequired: false,
        countyField: true,
        countryList: [
            { ISO_CC: "AE", countryName: "United Arab Emirates" }
        ],
        states: uae,
        showTax: false
    },
    'en-ph': {
        locale: "en-ph",
        localeWith_: "en_PH",
        defaultCurrency: "PHP",
        currencySign: "₱",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "PH", countryName: "Philippines" }
        ],
        states:philippines,
        showTax: false
    },
    'nb-no': {
        locale: "nb-no",
        localeWith_: "nb_NO",
        defaultCurrency: "NOK",
        currencySign: "kr",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "NO", countryName: "Norge" }
        ],
        states: [],
        showTax: false
    },
    'pl-pl': {
        locale: "pl-pl",
        localeWith_: "pl_PL",
        defaultCurrency: "PLN",
        currencySign: "zł",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "PL", countryName: "Polska" }
        ],
        states: [],
        showTax: false
    },
    'sv-se': {
        locale: "sv-se",
        localeWith_: "sv_SE",
        defaultCurrency: "SEK",
        currencySign: "kr",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "SE", countryName: "Sverige" }
        ],
        states: [],
        showTax: true
    },
    'da-dk': {
        locale: "da-dk",
        localeWith_: "da_DK",
        defaultCurrency: "DKK",
        currencySign: "kr",
        stateDropdown: false,   
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "DK", countryName: "Danmark" }
        ],
        states: [],
        showTax: true
    },
    'fi-fi': {
        locale: "fi-fi",
        localeWith_: "fi_FI",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: false,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "FI", countryName: "Suomi" }
        ],
        states: [],
        showTax: true
    },
    'nl-nl': {
        locale: "nl-nl",
        localeWith_: "nl_NL",
        defaultCurrency: "EUR",
        currencySign: "€",
        stateDropdown: true,
        postalCodeRequired: true,
        countyField: true,
        countryList: [
            { ISO_CC: "NL", countryName: "Nederland" },
            { ISO_CC: "BE", countryName: "België" }
        ],
        states: netherlands,
        showTax: false
    }
    
});

const urlLocale = window.location.pathname.split('/')[1] as LocaleKey;
const LocalizationContext = createContext<LocalizationContextType>(Localization[urlLocale] || Localization['en-us']);

export const useLocalization = () => {
    return React.useContext(LocalizationContext);
};

const LocalizationProvider = ({ children }: LocalizationProviderProps) => {
    const [localization, setLocalization] = useState(Localization[urlLocale] || Localization['en-us']); // Default language

    useEffect(() => {
        setLocalization(Localization[urlLocale]);
    }, []);

    return (
        <LocalizationContext.Provider value={localization}>
            {children}
        </LocalizationContext.Provider>
    );
};

export default LocalizationProvider;